import React, { useMemo } from "react";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/Components/ui/calendar";
import { Input } from "@/Components/ui/input";
import { Label } from "@/Components/ui/label";
import { Text } from "@/Components/ui/typography";
import { cn } from "@/lib/utils";
import {
  DEFAULT_MEETING_TIMEZONE,
  addMinutesToTimeValue,
  formatRescheduleDateLabel,
  formatRescheduleSlotDisplay,
  getManualMeetDurationLabel,
  getMinTimeForDate,
  getTodayDateString,
  normalizeRescheduleTimeLabel,
  normalizeTimeValue,
  parseRescheduleSuggestionLabel,
  resolveManualMeetDurationValue,
  resolveRescheduleTimezoneName,
  timeValueToMinutes,
} from "./meetingScheduleUtils";

const createSlotId = () =>
  `slot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createRescheduleSlotDraft = (overrides = {}) => ({
  id: createSlotId(),
  date: "",
  startTime: "",
  endTime: "",
  duration: "",
  ...overrides,
});

export const parseRescheduleDateInput = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  const [year, month, day] = raw.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

export const formatRescheduleDateInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatRescheduleSlotSummary = (slot, timezone = DEFAULT_MEETING_TIMEZONE) =>
  formatRescheduleSlotDisplay(slot, timezone);

const resolveDraftDuration = ({ startTime, endTime, duration }) => {
  const durationText = String(duration || "").trim();
  if (durationText && Number(durationText) >= 1 && Number.isFinite(Number(durationText))) {
    return durationText;
  }
  return resolveManualMeetDurationValue({ startTime, endTime }) || "";
};

const normalizeDraftTimes = ({
  date,
  startTime,
  endTime,
  duration,
}) => {
  const safeDuration = resolveDraftDuration({
    startTime,
    endTime,
    duration,
  });
  let nextStart = normalizeTimeValue(startTime);
  if (date && nextStart) {
    const minTime = normalizeTimeValue(getMinTimeForDate(date));
    if (
      minTime &&
      timeValueToMinutes(nextStart) != null &&
      timeValueToMinutes(nextStart) < timeValueToMinutes(minTime)
    ) {
      nextStart = minTime;
    }
  }
  let nextEnd = normalizeTimeValue(endTime);
  if (nextStart && nextEnd) {
    const startMinutes = timeValueToMinutes(nextStart);
    const endMinutes = timeValueToMinutes(nextEnd);
    if (startMinutes != null && endMinutes != null && endMinutes <= startMinutes) {
      nextEnd = "";
    }
  }
  const resolvedDuration =
    nextStart && nextEnd
      ? resolveManualMeetDurationValue({ startTime: nextStart, endTime: nextEnd }) || safeDuration
      : safeDuration;
  return {
    startTime: nextStart,
    endTime: nextEnd,
    duration: resolvedDuration,
  };
};

export const suggestionToRescheduleDraft = (suggestion, fallbackTimezone) => {
  if (!suggestion) return null;
  const timezone = resolveRescheduleTimezoneName(
    suggestion?.timezone || fallbackTimezone || DEFAULT_MEETING_TIMEZONE,
    fallbackTimezone || DEFAULT_MEETING_TIMEZONE
  );
  let date = String(suggestion?.date || "").trim();
  let startTime = normalizeTimeValue(suggestion?.start_time || suggestion?.startTime);
  let endTime = normalizeTimeValue(suggestion?.end_time || suggestion?.endTime);

  if ((!date || !startTime) && suggestion?.label) {
    const parsed = parseRescheduleSuggestionLabel(suggestion.label, timezone);
    if (parsed?.date && parsed?.start_time) {
      date = parsed.date;
      startTime = parsed.start_time;
      endTime = endTime || parsed.end_time || "";
    }
  }

  if (date && startTime) {
    const duration = resolveDraftDuration({ startTime, endTime });
    const normalized = normalizeDraftTimes({
      date,
      startTime,
      endTime,
      duration,
    });
    return createRescheduleSlotDraft({
      date,
      ...normalized,
    });
  }
  return null;
};

export const buildRescheduleSlotsFromBooking = ({
  scheduledStartTime,
  scheduledEndTime,
  timezone,
  formatDate,
  formatTime,
}) => {
  const date = formatDate?.(scheduledStartTime, timezone) || "";
  const startTime = formatTime?.(scheduledStartTime, timezone) || "";
  const endTime = formatTime?.(scheduledEndTime, timezone) || "";
  if (!date || !startTime) {
    return {
      draft: createRescheduleSlotDraft(),
      slots: [],
      primarySlotId: "",
    };
  }

  const duration = resolveDraftDuration({ startTime, endTime });
  const normalized = normalizeDraftTimes({
    date,
    startTime,
    endTime,
    duration,
  });
  const initialSlot = createRescheduleSlotDraft({
    date,
    ...normalized,
  });

  return {
    draft: createRescheduleSlotDraft({ date, ...normalized }),
    slots: [initialSlot],
    primarySlotId: initialSlot.id,
  };
};

const InterviewRescheduleSlotEditor = ({
  timezone = DEFAULT_MEETING_TIMEZONE,
  draft,
  onDraftChange,
  currentSlot = null,
  suggestedSlots = [],
  selectedSuggestionId = "",
  onSelectSuggestedSlot,
  availabilityMode = false,
  availabilityActorLabel = "Participant",
  disabled = false,
  minDate = getTodayDateString(),
}) => {
  const selectedCalendarDate = useMemo(
    () => parseRescheduleDateInput(draft?.date),
    [draft?.date]
  );
  const minCalendarDate = useMemo(
    () => parseRescheduleDateInput(minDate),
    [minDate]
  );
  const effectiveMinTime = useMemo(
    () => normalizeTimeValue(getMinTimeForDate(draft?.date)),
    [draft?.date]
  );
  const minEndTime = useMemo(() => {
    const start = normalizeTimeValue(draft?.startTime);
    if (!start) return "";
    return addMinutesToTimeValue(start, 1);
  }, [draft?.startTime]);
  const draftEndTime = normalizeTimeValue(draft?.endTime);
  const selectedAvailabilityWindow = useMemo(
    () =>
      suggestedSlots.find((item) => item.id === selectedSuggestionId) || null,
    [selectedSuggestionId, suggestedSlots]
  );
  const windowStartTime = normalizeRescheduleTimeLabel(
    selectedAvailabilityWindow?.start_time || selectedAvailabilityWindow?.startTime
  );
  const windowEndTime = normalizeRescheduleTimeLabel(
    selectedAvailabilityWindow?.end_time || selectedAvailabilityWindow?.endTime
  );
  const windowDateMatchesDraft =
    availabilityMode &&
    selectedAvailabilityWindow?.date &&
    draft?.date &&
    selectedAvailabilityWindow.date === draft.date;
  const windowStartMinutes = timeValueToMinutes(windowStartTime);
  const windowEndMinutes = timeValueToMinutes(windowEndTime);
  const hasValidWindowRange =
    windowDateMatchesDraft &&
    windowStartMinutes != null &&
    windowEndMinutes != null &&
    windowEndMinutes > windowStartMinutes;
  const startTimeMin = hasValidWindowRange ? windowStartTime : effectiveMinTime;
  const startTimeMax = hasValidWindowRange ? windowEndTime : "";
  const endTimeMax = hasValidWindowRange ? windowEndTime : "";
  const selectedDateLabel = formatRescheduleDateLabel(draft?.date);
  const hasCompleteSelection = Boolean(
    draft?.date && draft?.startTime && draftEndTime
  );
  const currentSummary = useMemo(() => {
    if (!currentSlot?.date || !currentSlot?.startTime) return "";
    return formatRescheduleSlotDisplay(currentSlot, timezone);
  }, [currentSlot, timezone]);
  const newSummary = useMemo(() => {
    if (!hasCompleteSelection) return "Pick a date, start time, and end time";
    return formatRescheduleSlotDisplay(
      {
        ...draft,
        endTime: draftEndTime,
      },
      timezone
    );
  }, [draft, draftEndTime, hasCompleteSelection, timezone]);

  const interviewLengthLabel = useMemo(() => {
    if (!draft?.startTime || !draftEndTime) {
      return "Enter any end time after the start time.";
    }
    const startMinutes = timeValueToMinutes(draft.startTime);
    const endMinutes = timeValueToMinutes(draftEndTime);
    if (
      startMinutes != null &&
      endMinutes != null &&
      endMinutes <= startMinutes
    ) {
      return "End time must be after the start time.";
    }
    const durationValue = resolveManualMeetDurationValue({
      startTime: draft.startTime,
      endTime: draftEndTime,
    });
    const label = getManualMeetDurationLabel(durationValue);
    return label ? `Interview length: ${label}` : "Interview length: —";
  }, [draft?.startTime, draftEndTime]);

  const updateDraft = (patch) => {
    onDraftChange?.({ ...draft, ...patch });
  };

  const applyStartTime = (startTime) => {
    const normalizedStart = normalizeTimeValue(startTime);
    if (!normalizedStart) {
      return;
    }
    const currentEnd = normalizeTimeValue(draft?.endTime);
    const startMinutes = timeValueToMinutes(normalizedStart);
    const endMinutes = timeValueToMinutes(currentEnd);
    let nextEnd = currentEnd;
    if (startMinutes != null && endMinutes != null && endMinutes <= startMinutes) {
      nextEnd = "";
    }
    const duration =
      normalizedStart && nextEnd
        ? resolveManualMeetDurationValue({
            startTime: normalizedStart,
            endTime: nextEnd,
          })
        : draft?.duration;
    updateDraft({
      startTime: normalizedStart,
      endTime: nextEnd,
      duration,
    });
  };

  const applyEndTime = (endTime) => {
    const normalizedEnd = normalizeTimeValue(endTime);
    if (!normalizedEnd) {
      return;
    }
    const duration = resolveManualMeetDurationValue({
      startTime: draft?.startTime,
      endTime: normalizedEnd,
    });
    updateDraft({
      endTime: normalizedEnd,
      duration,
    });
  };

  const applyDate = (date) => {
    if (!date) return;
    const nextDate = formatRescheduleDateInput(date);
    const normalized = normalizeDraftTimes({
      date: nextDate,
      startTime: draft?.startTime,
      endTime: draft?.endTime,
      duration: draft?.duration,
    });
    updateDraft({
      date: nextDate,
      ...normalized,
    });
  };

  return (
    <div className="space-y-4">
      {currentSummary ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <Text size="sm" className="font-semibold text-slate-900">
            Schedule change
          </Text>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
              <Text
                size="xs"
                className="font-medium uppercase tracking-wide text-slate-500"
              >
                Current
              </Text>
              <Text size="sm" className="mt-1 font-medium text-slate-900">
                {currentSummary}
              </Text>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
              <Text
                size="xs"
                className="font-medium uppercase tracking-wide text-emerald-700"
              >
                New
              </Text>
              <Text size="sm" className="mt-1 font-medium text-emerald-950">
                {newSummary}
              </Text>
            </div>
          </div>
        </div>
      ) : null}

      {suggestedSlots.length ? (
        <div className="rounded-xl border border-sky-200 bg-sky-50/80 p-4">
          <Text size="sm" className="font-semibold text-sky-950">
            {availabilityMode
              ? `Shared availability from ${availabilityActorLabel}`
              : "Suggested times"}
          </Text>
          <Text size="xs" className="mt-1 text-sky-800">
            {availabilityMode
              ? "Select an availability window, then pick the final interview start and end time inside it."
              : "Click one of the suggested times to fill the calendar below."}
          </Text>
          <div className="mt-3 grid gap-2">
            {suggestedSlots.map((suggestion) => {
              const isSelected = suggestion.id === selectedSuggestionId;
              return (
                <button
                  key={suggestion.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelectSuggestedSlot?.(suggestion)}
                  className={cn(
                    "rounded-lg border px-3 py-2 text-left transition-colors",
                    isSelected
                      ? "border-emerald-300 bg-emerald-50 ring-1 ring-emerald-200"
                      : "border-sky-200 bg-white hover:border-sky-300 hover:bg-sky-50/60"
                  )}
                >
                  <Text size="sm" className="font-medium text-slate-900">
                    {suggestion.label}
                  </Text>
                  {availabilityMode ? (
                    <Text size="xs" className="mt-0.5 text-slate-500">
                      Availability window
                    </Text>
                  ) : null}
                  {suggestion.date && suggestion.start_time ? (
                    <Text size="xs" className="mt-0.5 text-slate-500">
                      {formatRescheduleSlotSummary(
                        {
                          date: suggestion.date,
                          startTime: suggestion.start_time,
                          endTime: suggestion.end_time,
                        },
                        suggestion.timezone || timezone
                      )}
                    </Text>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2 text-slate-700">
          <CalendarDays className="h-4 w-4" />
          <Text size="sm" className="font-semibold">
            {availabilityMode
              ? "Pick final interview time"
              : "Pick a new interview date"}
          </Text>
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="mx-auto rounded-lg border border-slate-200 bg-slate-50 p-2 shadow-sm lg:mx-0">
            <Calendar
              mode="single"
              selected={selectedCalendarDate}
              onSelect={applyDate}
              disabled={(date) => {
                if (!date || !minCalendarDate) return false;
                const day = new Date(
                  date.getFullYear(),
                  date.getMonth(),
                  date.getDate()
                );
                const minDay = new Date(
                  minCalendarDate.getFullYear(),
                  minCalendarDate.getMonth(),
                  minCalendarDate.getDate()
                );
                return day < minDay;
              }}
              initialFocus
            />
          </div>

          <div className="flex-1 space-y-4">
            {selectedDateLabel ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                <Text
                  size="xs"
                  className="font-medium uppercase tracking-wide text-emerald-700"
                >
                  Selected date
                </Text>
                <Text size="sm" className="font-semibold text-emerald-900">
                  {selectedDateLabel}
                </Text>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-3">
                <Text size="sm" className="text-slate-500">
                  Choose a date on the calendar to continue.
                </Text>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="reschedule-start-time">
                  {availabilityMode ? "Interview Start Time" : "Availability Start Time"}
                </Label>
                <Input
                  id="reschedule-start-time"
                  type="time"
                  value={draft?.startTime || ""}
                  min={startTimeMin || undefined}
                  max={startTimeMax || undefined}
                  disabled={disabled || !draft?.date}
                  onChange={(event) => applyStartTime(event.target.value)}
                />
                <Text size="xs" className="text-slate-500">
                  {availabilityMode
                    ? "Choose the final interview start time inside the selected availability window."
                    : "Enter any start time — no fixed slot length required."}
                </Text>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reschedule-end-time">
                  {availabilityMode ? "Interview End Time" : "Availability End Time"}
                </Label>
                <Input
                  id="reschedule-end-time"
                  type="time"
                  value={draftEndTime || ""}
                  min={minEndTime || undefined}
                  max={endTimeMax || undefined}
                  disabled={disabled || !draft?.date || !draft?.startTime}
                  onChange={(event) => applyEndTime(event.target.value)}
                />
                <Text size="xs" className="text-slate-500">
                  {interviewLengthLabel}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewRescheduleSlotEditor;
